import { apiFetch } from "../api.js";

/**
 * Phase 13.5 — `/v1/me/settings` SPA api-module.
 *
 * The `user_settings` row owns AI billing/BYOK plus three Phase-13.5
 * additive surfaces: dashboard layout, per-template email opt-outs, and
 * recipient routing within a household. The AI-specific endpoints at
 * `/v1/settings/ai` stay; this module wraps the orthogonal `/v1/me/*`
 * endpoints.
 *
 * GET returns the full row with encrypted BYOK key bytes redacted to
 * boolean flags. PATCH accepts a partial body — only fields PRESENT in
 * the object are written; pass `dashboard_layout: null` to delete.
 */

export interface DashboardWidgetEntry {
  readonly widget_id: string;
  readonly position: number;
  readonly hidden?: boolean;
  readonly instance_config?: Record<string, unknown>;
}

export interface DashboardLayout {
  readonly widgets: readonly DashboardWidgetEntry[];
}

export interface MySettingsResponse {
  readonly user_id: string;
  readonly ai_provider: string;
  readonly ai_billing_mode: string;
  readonly default_claude_model: string | null;
  readonly default_openai_model: string | null;
  readonly default_bedrock_model: string | null;
  readonly monthly_token_cap: number;
  readonly anthropic_byok_configured: boolean;
  readonly openai_byok_configured: boolean;
  readonly dashboard_layout: DashboardLayout | null;
  readonly email_opt_outs: Readonly<Record<string, boolean>>;
  readonly email_routes: Readonly<Record<string, string>>;
  readonly updated_at: string | null;
}

export interface PatchMySettingsBody {
  // `null` deletes the field; absent leaves it untouched.
  readonly dashboard_layout?: DashboardLayout | null;
  readonly email_opt_outs?: Record<string, boolean>;
  readonly email_routes?: Record<string, string>;
}

export function getMySettings(): Promise<MySettingsResponse> {
  return apiFetch("/v1/me/settings");
}

export function patchMySettings(
  body: PatchMySettingsBody,
): Promise<{ ok: boolean; changes: number }> {
  return apiFetch("/v1/me/settings", {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}
